import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
 Col, Container, Row, Button, 
} from 'reactstrap';
import DataReactTable from './components/DataReactTable';
import CreateTableData, { CreatePaymentTableData } from '../CreateData';
import env from '../../../Env';
import { get, post } from '../../../utils/api/base';
import AttendeeNoteModal from './components/AttendeeNoteModal';
import ReportFilter, { ReportType } from '../../../shared/components/filter/ReportFilter';
import history from '../../../utils/history';
import { getExportOrder } from './exportOrdering';
import './index.scss';

const getFilterParams = filters => Object.entries(filters).reduce((params, filter) => {
  const key = filter[0];
  const values = filter[1]?.value;

  if (values?.length && values[0].value) {
    return `${params}${values.reduce((listParams, current) => {
      if (current.value) {
        return `${listParams}${key}=${current.value}&`;
      }
      return listParams;
    }, '')}`;
  }

  return params;
}, '');

const getQueryFilters = (tableData) => {
  const filters = {};
  const urlSearchParams = new URLSearchParams(window.location.search);

  urlSearchParams.forEach((paramValue, paramKey) => {
    if (paramKey !== 'adminReportId' && paramKey !== 'reportType') {
      const filter = tableData.filters.find(el => el.key === paramKey);

      if (filter) {
        const ft = filter.filterType;
        const filterValues = [{ value: paramValue }];

        if (filters[paramKey]) {
          const { value, filterType } = filters[paramKey];
          filters[paramKey] = { value: filterValues.concat(value), filterType };
        } else {
          filters[paramKey] = { value: filterValues, filterType: ft };
        }
      }
    }
  });

  return filters;
};

const getClientId = urlParams => urlParams?.split('?')[0];

const stripCurrency = (value) => {
  if (typeof value !== 'string') return value;
  return value.replace(/[$£€¥₹₩₽₫₪₦₴₱₲₵]/g, '').trim();
};

const formatDate = (value) => {
  if (!value) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const DataTable = () => {
  const [noteModalVisible, setNoteModalVisible] = useState(false);
  const [currentlySelectedAttendee, setCurrentlySelectedAttendee] = useState('');

  const toggleAttendeeNotesModal = (payceEventAttendeeId) => {
    setCurrentlySelectedAttendee(payceEventAttendeeId);
    setNoteModalVisible(payceEventAttendeeId !== null);
  };

  const searchParams = new URLSearchParams(window.location.search);
  const adminReportId = searchParams.get('adminReportId');
  const [currentId, setCurrentId] = useState(adminReportId);

  const reportType = +(searchParams.get('reportType'));

  const attendeeReportTableData = CreateTableData(toggleAttendeeNotesModal);
  const paymentReportTableData = CreatePaymentTableData();
  const [tableData, setTableData] = useState(reportType === ReportType.Attendee
    ? attendeeReportTableData : paymentReportTableData);

  const [totalRecords, setTotalRecords] = useState(0);
  const [filterValues, setFilterValues] = useState({});

  const [reportDetailLoading, setReportDetailLoading] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);

  const [displayedRows, setDisplayedRows] = useState([]);

  const [page, setPage] = useState(1);

  const attendeeFilters = getQueryFilters(CreateTableData(toggleAttendeeNotesModal));
  const paymentFilters = getQueryFilters(CreatePaymentTableData());
  const [filters, setFilters] = useState(reportType === ReportType.Attendee ? attendeeFilters : paymentFilters);

  const defaultSortProperty = reportType === ReportType.Attendee ? 'startDate' : 'datePaidUtc';
  const [sortedColumn, setSortedColumn] = useState({ sortByProperty: defaultSortProperty, sortDirection: 'desc' });

  const { urlParams } = useParams();
  const clientId = getClientId(urlParams);

  const [originalReportName, setOriginalReportName] = useState('');
  const [originalIsSystemReport, setOriginalIsSystemReport] = useState(false);

  const location = useLocation();

  const [shouldFetch, setShouldFetch] = useState(true);
  const [showReportFilters, setShowReportFilters] = useState(false);

  const setHasNotesForCurrentAttendee = (hasNotes) => {
    tableData.tableRowsData.filter(x => x.payceEventAttendeeId === currentlySelectedAttendee)[0].hasNotes = hasNotes;
  };

  useEffect(() => {
    if (currentId !== adminReportId) {
      setCurrentId(adminReportId);
      setFilters(reportType === ReportType.Attendee ? attendeeFilters : paymentFilters);
      setTableData(reportType === ReportType.Attendee ? attendeeReportTableData : paymentReportTableData);
      setPage(1);
      setOriginalReportName('');
      setShowReportFilters(false);
      setSortedColumn({
        sortByProperty: reportType === ReportType.Attendee ? 'startDate' : 'datePaidUtc',
        sortDirection: 'desc',
      });
      setShouldFetch(true);
    }
  }, [adminReportId, currentId, attendeeFilters, paymentFilters, attendeeReportTableData,
    paymentReportTableData, reportType]);

  useEffect(() => {
    const fetchData = async () => {
      setReportDetailLoading(true);
      const endpoint = encodeURI(`${env.baseApiUrl}/api/reports/${clientId}/${adminReportId}`);
      get(endpoint).then((res) => {
        const { data } = res;
        setOriginalReportName(data.reportName);
        setOriginalIsSystemReport(data.isSystemReport);
        setReportDetailLoading(false);
      });
    };

    fetchData();
  }, [clientId, adminReportId]);
  useEffect(() => {
    const fetchData = async () => {
      setResultsLoading(true);

      const queryString = `?reportType=${reportType}`;
      const endpoint = encodeURI(`${env.baseApiUrl}/api/reports/${clientId}${queryString}`);

      get(endpoint).then((res) => {
        const { data } = res;
        const tableRowsData = data.rows.map(x => ({
          ...x,
          eventName: {
            value: x.eventName,
            link: x.payceEventId !== null && (x.imported === undefined || x.imported === 'False')
              ? `https://${x.client}/portal#/events/${x.payceEventId}`
              : '',
          },
          payceEventId: x.payceEventId === '00000000-0000-0000-0000-000000000000' ? 'N/A' : x.payceEventId,
        }));

        setTableData(previous => ({ ...previous, tableRowsData }));
        setFilterValues(data.filterValues);
        setResultsLoading(false);
      });
    };

    if (shouldFetch) {
      fetchData();
      setShouldFetch(false);
    }
  }, [clientId, adminReportId, reportType, shouldFetch]);

  useEffect(() => {
    let rows = tableData?.tableRowsData || [];

    rows = rows.filter(row => Object.entries(filters).every(([key, filterObj]) => {
      const activeValues = filterObj?.value ?? [];
      if (!activeValues.length) return true;

      const cell = row[key];
      const cellStr = typeof cell === 'string' ? cell.toLowerCase() : String(cell ?? '').toLowerCase();

      return activeValues.some((v) => {
        const fVal = typeof v.value === 'string' ? v.value.toLowerCase() : String(v.value ?? '').toLowerCase();
        return cellStr.includes(fVal);
      });
    }));

    const { sortByProperty, sortDirection } = sortedColumn;
    rows = [...rows].sort((a, b) => {
    const aRaw = a[sortByProperty];
    const bRaw = b[sortByProperty];

    if (aRaw == null && bRaw == null) return 0;
    if (aRaw == null) return sortDirection === 'asc' ? -1 : 1;
    if (bRaw == null) return sortDirection === 'asc' ? 1 : -1;

    const aDate = new Date(aRaw);
    const bDate = new Date(bRaw);
    if (aDate.toString() !== 'Invalid Date' && bDate.toString() !== 'Invalid Date') {
        return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
    }

    const aStr = String(aRaw).toLowerCase();
    const bStr = String(bRaw).toLowerCase();
    return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
});

    setDisplayedRows(rows);
    setTotalRecords(rows.length);
    setPage(1);
  }, [tableData.tableRowsData, filters, sortedColumn]);

  const { t } = useTranslation('common');

  const handleFiltersUpdate = (key, value, filterType) => {
    const newFilters = { ...filters };
    newFilters[key] = { value, filterType };

    const adminReportParam = adminReportId ? `?adminReportId=${adminReportId}&reportType=${reportType}&` : '';
    const queryParameters = getFilterParams(newFilters);
    history.push({ search: adminReportParam + queryParameters });

    setFilters(newFilters);
  };

  const handleReportWizardSubmit = (name, isSystemReport, selectedFilters) => {
    setReportDetailLoading(true);

    setOriginalReportName(name);
    setOriginalIsSystemReport(isSystemReport);

    const adminReportParam = adminReportId ? `?adminReportId=${adminReportId}&reportType=${reportType}&` : '';
    const queryParameters = selectedFilters.reduce((acc, { key, value }) => `${acc}${key}=${value}&`, '');

    const basePath = location.pathname.split('?')[0];
    history.push({ pathname: `${basePath}`, search: adminReportParam + queryParameters });

    const endpoint = encodeURI(`${env.baseApiUrl}/api/reports/${clientId}`);
      const body = JSON.stringify(
          {
              name, isSystemReport, queryParameters, clientId, adminReportId,
          },
      );

    post(endpoint, body).then(() => {
      if (location.reportProps?.reloadSidebar) {
        location.reportProps.reloadSidebar();
      }
    });

    const newFilters = selectedFilters.reduce((acc, cur) => {
      if (acc[cur.key]) {
        acc[cur.key] = {
          filterType: acc[cur.key].filterType,
          value: acc[cur.key].value.concat({ value: cur.value }),
        };
      } else {
        acc[cur.key] = { filterType: cur.filterType, value: [{ value: cur.value }] };
      }
      return acc;
    }, {});

    setFilters(newFilters);
    setShouldFetch(true);
    setReportDetailLoading(false);
  };

  const convertToCSV = (rows, order) => {
    if (!rows?.length) return '';

    const headers = order || Object.keys(rows[0]).filter(key => key !== 'eventName');
    if (!order) headers.push('eventName');

    const headerRow = headers.join(',');

    const dataRows = rows.map(row => headers.map((header) => {
      const value = header === 'eventName' ? row.eventName?.value : row[header];

      if (value === null || value === undefined) return '';

      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }

      return stringValue;
    }).join(','));

    return [headerRow, ...dataRows].join('\n');
  };

  const handleExport = () => {
    const rows = displayedRows;

    if (!rows?.length) {
      console.warn('No data to export');
      return;
    }

    const exportOrder = getExportOrder(reportType);

    const dateColumns = ['startDate', 'endDate', 'datePaidUtc'];

    const cleanedRows = rows.map(row =>
      Object.fromEntries(
        Object.entries(row).map(([key, value]) => {
          let processedValue = value;
          
          if (dateColumns.includes(key)) {
            processedValue = formatDate(value);
          } else {
            processedValue = stripCurrency(value);
          }
          
          return [key, processedValue];
        }),
      ));

    const csv = convertToCSV(cleanedRows, exportOrder);
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const file = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = file;
    link.setAttribute('download', 'report.csv');
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(file);
  };

  const handleSort = (sortData) => {
    setSortedColumn(sortData);
  };

  const displayedTableData = { ...tableData, tableRowsData: displayedRows };

  return (
    <Container className="admin-report">
      <Row>
        <Col md={6}>
          <h3 className="page-title">{t('Reports')} - {originalReportName}</h3>
        </Col>
        <Col md={6}>
          <div className="admin-report__button-group-right">
            <Button
              style={{ marginRight: '15px' }}
              color="primary"
              onClick={() => setShowReportFilters(!showReportFilters)}
            >
              {showReportFilters ? 'Hide filters' : 'Show filters'}
            </Button>
            <Button
              color="success"
              onClick={handleExport}
              download="report.csv"
            >
              Export report
            </Button>
          </div>
        </Col>
      </Row>
      <Row>
        {!reportDetailLoading && showReportFilters && (
          <ReportFilter
            filterValues={filterValues}
            queryFilters={filters}
            handleSubmit={handleReportWizardSubmit}
            reportName={originalReportName}
            setReportName={setOriginalReportName}
            isSystemReport={originalIsSystemReport}
            setIsSystemReport={setOriginalIsSystemReport}
          />
        )}
      </Row>
      <Row>
        <DataReactTable
          reactTableData={displayedTableData}
          page={page}
          setPage={setPage}
          totalRecords={totalRecords}
          loading={resultsLoading}
          filters={filters}
          handleFiltersUpdate={handleFiltersUpdate}
          filterValues={filterValues}
          handleSort={handleSort}
          sortedColumn={sortedColumn}
        />
      </Row>
      <AttendeeNoteModal
        visible={noteModalVisible}
        toggleVisiblity={toggleAttendeeNotesModal}
        payceEventAttendeeId={currentlySelectedAttendee}
        updateHasNotes={setHasNotesForCurrentAttendee}
      />
    </Container>
  );
};

export default DataTable;
